import { BatchEvaluationResponse } from '@flipt-io/flipt/dist/evaluation/models'

import config from 'config'
import { pathOr } from 'ramda'

import { camelize } from 'src/lib/util/camelize'

import { DEFAULT_FLIPT_CONTEXT } from '../constants'
import { FeatureFlagContext, FeatureFlags } from '../types'
import { FLIPT_API_CLIENT } from './flipt-client'

const buildFlagRequest = (flagKeys: string[], context: FeatureFlagContext) => {
  return flagKeys.map(flagKey => ({
    namespaceKey: config.NEXT_PUBLIC_FLIPT_NAMESPACE || 'develop',
    flagKey,
    entityId: context.user_id,
    context
  }))
}

const mapFliptBatchEvaluationResponseToObj = (
  response: BatchEvaluationResponse
) => {
  return response.responses.reduce((acc: Record<string, boolean>, res) => {
    if (res.booleanResponse) {
      const flagKey = camelize(res.booleanResponse.flagKey)
      acc[flagKey] = res.booleanResponse?.enabled
    } else if (res.variantResponse) {
      const flagKey = camelize(res.variantResponse.flagKey)
      acc[flagKey] = res.variantResponse.match
    } else if (res.errorResponse) {
      const flagKey = camelize(res.errorResponse.flagKey)
      acc[flagKey] = false
    }
    return acc
  }, {})
}

export const fetchFeatureFlags = async (
  pageProps: object = {},
  networkName: string
) => {
  if (!config.NEXT_PUBLIC_FLIPT_TOKEN) return {}

  const userId = pathOr(
    DEFAULT_FLIPT_CONTEXT.user_id,
    ['user', ':user/squuid'],
    pageProps
  )

  const network =
    pathOr(
      DEFAULT_FLIPT_CONTEXT.network_name,
      ['network', ':network/name'],
      pageProps
    ) || networkName

  const boardId = pathOr(
    DEFAULT_FLIPT_CONTEXT.board_id,
    ['board', ':board/squuid'],
    pageProps
  )

  try {
    const rawFlags = (
      await FLIPT_API_CLIENT.flags.listFlags(config.NEXT_PUBLIC_FLIPT_NAMESPACE)
    ).flags

    const flagsKeys = rawFlags.map(({ key }) => key)

    const flagsRequests = buildFlagRequest(flagsKeys, {
      user_id: userId,
      network_name: network,
      board_id: boardId
    })

    const flagsBatchEvaluationResponse =
      await FLIPT_API_CLIENT.evaluation.batch({
        requests: flagsRequests
      })

    const flagsValues = mapFliptBatchEvaluationResponseToObj(
      flagsBatchEvaluationResponse
    )

    const flagsMap = rawFlags.reduce((acc, flag) => {
      const { key, description } = flag
      acc[camelize(key)] = {
        description,
        value: flagsValues[camelize(key)]
      }
      return acc
    }, {} as FeatureFlags)

    return flagsMap
  } catch {
    return {}
  }
}
